/**
 * src/App.tsx — route table.
 *
 * Constraints from the migration brief:
 *   - "Create one route for the current Italy forecast page at its existing path"
 *   - Information architecture preserved through Phase 4
 *
 * Today: `/forecast/italy` is the canonical path. The legacy Italy dashboard
 * mounts there. Anything else falls back to it (no 404 view yet — Phase 8).
 *
 * NOT routed yet (later phases):
 *   - `/me`, `/u/:userId` (Phase 6)
 *   - `/admin/users` (Phase 5)
 *   - `/market/:marketId` (Phase 7)
 *
 * (Phase 4d: the parked src/pages/Dashboard.tsx was deleted — Phase 4 never
 * graduated it, and it accumulated drift vs. the production ForecastItaly.
 * If a v2 Dashboard graduates in the future, derive it fresh from the
 * current ForecastItaly instead of resurrecting the parked file. Git
 * history retains the original if needed for reference.)
 *
 * Phase 3 addition: `/__lab` route for the primitive showcase, gated to
 * `import.meta.env.DEV`. Tree-shaken in production builds.
 */
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
// Phase 4e — global lang preference. Used by the footer "Platform map" link.
import { useLang } from "./lib/lang";
// Phase 4 sub-step 9 → Phase H: Dashboard moved from src/legacy/app.tsx
// to src/views/ForecastMarket.tsx (was ForecastItaly.tsx until Phase H —
// renamed when the file's logic was already market-agnostic via
// window.MARKET_CONFIG; keeping the name "ForecastItaly" was misleading).
// The internal export is still named `Dashboard` for back-compat with
// window.Dashboard publishes. ErrorBoundary co-resides for now; future
// cleanup may extract it to src/components/composed/ErrorBoundary.tsx.
import { Dashboard as ForecastMarket, ErrorBoundary } from "./views/ForecastMarket";
// Phase 5.5: admin user management
import AdminUsers from "./views/AdminUsers";
// Phase 5.7b: audit log viewer
import AdminAudit from "./views/AdminAudit";
// Phase 6: user profile (/me + /u/:userId)
import UserProfile, { MeRedirect } from "./views/UserProfile";
// Phase 8: friendly 404
import NotFound from "./views/NotFound";
// Phase 7 sub-step 1: market route wrapper (italy → ForecastItaly today)
import MarketDashboard from "./views/MarketDashboard";
// Phase 4f — /map route + global footer link. Listed once, linked from
// the footer rendered below; lets anyone unfamiliar with the URL space
// browse the whole platform.
import PlatformMap from "./views/PlatformMap";
// Phase 3a — single toast viewport for the whole app. window.toast in
// legacy/primitives.tsx now delegates to the same primitive, so legacy
// callers (ForecastItaly bare-name `toast.success(...)`) and new callers
// (state-storage.ts, hooks.ts, etc.) all render through this surface.
import { ToastViewport } from "./components/ui/Toast";
// Phase A1 (post-engagement migration) — persistent shell + sidebar
// wraps every route. The topbar is intentionally null in this phase;
// per-view chrome stays as-is. Phase A2 wires the topbar + strips
// bespoke per-view headers.
import AppShell from "./components/shell/AppShell";
import AppSidebar from "./components/shell/AppSidebar";
// Phase A2 — global topbar for non-forecast routes. Suppressed on
// /forecast/italy + /market/* because ForecastItaly's bespoke .head
// already renders a richer top bar (refresh status, KPI sticky, value-
// mode, scenarios, timeframe). Phase B will harmonize.
import AppTopbar from "./components/shell/AppTopbar";

// Lab is DEV-only. The lazy import + DEV gate together let Vite tree-shake
// the entire showcase out of production bundles. If DEV is false, the import
// promise never resolves because the Route element is never rendered.
const Lab = import.meta.env.DEV ? lazy(() => import("./views/Lab")) : null;

export default function App() {
  return (
    <ErrorBoundary>
      <Shell>
        <Routes>
          <Route path="/forecast/italy" element={<ForecastMarket />} />
          {/* Phase 7 — multi-market route. Today only italy renders; others
              see a "not yet supported" page. ForecastItaly genericization
              follows in later sub-steps. */}
          <Route path="/market/:marketId" element={<MarketDashboard />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/audit" element={<AdminAudit />} />
          <Route path="/me" element={<MeRedirect />} />
          <Route path="/u/:userId" element={<UserProfile />} />
          {/* Phase 4f — bilingual sitemap, surfaced via the footer link below. */}
          <Route path="/map" element={<PlatformMap />} />
          {Lab ? (
            <Route
              path="/__lab"
              element={
                <Suspense fallback={<div style={{ padding: 32 }}>Loading lab…</div>}>
                  <Lab />
                </Suspense>
              }
            />
          ) : null}
          {/* Legacy paths Marco / colleagues may have bookmarked */}
          <Route path="/italy-forecast.html" element={<Navigate to="/forecast/italy" replace />} />
          <Route path="/" element={<Navigate to="/forecast/italy" replace />} />
          {/* Phase 8: real 404 instead of silently swallowing unknown URLs */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Shell>
      {/* Phase 3a — single toast surface. Replaces the legacy
          window.ToastProvider conditional mount; legacy primitives.tsx
          now delegates window.toast.* to the new toast.* under the
          hood, so this <ToastViewport /> is the only renderer. */}
      <ToastViewport />
      {/* Phase 4f — global footer with a single "Platform map" link.
          Suppressed on the map page itself (don't show a link to the
          page you're on) and on /__lab (the dev showcase doesn't need
          navigation chrome). Low-contrast on purpose — discoverable
          but not competing with content. */}
      <AppFooter />
    </ErrorBoundary>
  );
}

/* Phase A2 — Shell wrapper. Renders AppShell with persistent sidebar
   for every route EXCEPT /__lab (dev showcase doesn't need it).
   Topbar shows on all routes EXCEPT /forecast/italy and /market/*, where
   ForecastItaly's bespoke .head still owns the top-of-page chrome. Phase
   B's PageHeader primitive will eventually unify them. */
function Shell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  // Lab is a primitives showcase — no shell chrome.
  if (pathname === "/__lab") return <>{children}</>;
  // ForecastItaly routes keep their own header for now; suppress global topbar.
  const isForecastRoute =
    pathname === "/forecast/italy" || pathname.startsWith("/market/");
  return (
    <AppShell
      sidebar={<AppSidebar />}
      topbar={isForecastRoute ? null : <AppTopbar />}
    >
      {children}
    </AppShell>
  );
}

function AppFooter() {
  const lang = useLang();
  const { pathname } = useLocation();
  if (pathname === "/map" || pathname === "/__lab") return null;
  return (
    <footer
      style={{
        padding: "var(--space-3) var(--page-gutter)",
        marginTop: "var(--space-6)",
        textAlign: "center",
        color: "var(--text-muted)",
        fontSize: "var(--fs-body-sm)",
        borderTop: "1px solid var(--border)",
      }}
    >
      <Link
        to="/map"
        style={{ color: "var(--text-muted)", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "3px" }}
      >
        {lang === "it" ? "Mappa della piattaforma" : "Platform map"}
      </Link>
    </footer>
  );
}
